import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import Paper from "@mui/material/Paper";
import React from "react";
import { useCurrencyFormatter } from "../../hooks/useCurrencyFormatter";
import { ModeInsightsProps } from "../../types/insights";
import { yearsAndMonths } from "../../utils";
import ModeInsightsCard from "./ModeInsightsCard";
import StarIcon from "./StarIcon";

export default function InterestRateInsights({ model }: ModeInsightsProps) {
  const c = useCurrencyFormatter();
  const nSimulations = model.results.length;

  const maxInterestRate = Math.max(
    ...model.results.simulations.map((sim) => sim.mortgage.interestRate),
  );
  const minInterestRate = Math.min(
    ...model.results.simulations.map((sim) => sim.mortgage.interestRate),
  );

  const cheapestSimulation = model.results.simulations.reduce(
    (cheapest, sim) => {
      const simTotalCost = sim.totalCostOfMortgage;
      const cheapestTotalCost = cheapest.totalCostOfMortgage;
      return simTotalCost < cheapestTotalCost ? sim : cheapest;
    },
    model.results.simulations[0],
  );

  // Determine if user provided a fixed term (with calculated monthly payment)
  // or a fixed monthly payment (with calculated term)
  const firstSimParams = model.results.simulations[0]?.initialParams;
  const isFixedTermMode =
    firstSimParams?.termYears !== undefined &&
    firstSimParams?.desiredMonthlyPayment === undefined;

  // Calculate metrics for each simulation
  const simulationsWithMetrics = model.results.simulations.map((sim) => {
    const interestRate = sim.mortgage.interestRate;
    const termMonths = sim.mortgage.termMonths;
    const monthlyPayment = sim.mortgage.desiredMonthlyPayment;
    const interestMultiplier = sim.totalInterestPaid / sim.principal;
    const savingsPercentage = (
      (sim.totalInterestEarned / sim.totalInterestPaid) *
      100
    ).toFixed(1);
    return {
      sim,
      interestRate,
      termMonths,
      monthlyPayment,
      interestMultiplier,
      savingsPercentage,
    };
  });

  // Sort by interest rate (lowest first)
  const sortedByRate = [...simulationsWithMetrics].sort(
    (a, b) => a.interestRate - b.interestRate,
  );

  const lowestRate = sortedByRate[0];
  const highestRate = sortedByRate[sortedByRate.length - 1];

  // Calculate cost difference between highest and lowest rate
  const costDifference =
    highestRate.sim.totalCostOfMortgage - lowestRate.sim.totalCostOfMortgage;
  const costPerPercentPoint =
    costDifference / (highestRate.interestRate - lowestRate.interestRate);

  return (
    <ModeInsightsCard>
      <Typography variant="body2" gutterBottom>
        <strong>{nSimulations}</strong> simulations were compared with interest
        rates ranging from <strong>{minInterestRate}%</strong> to{" "}
        <strong>{maxInterestRate}%</strong>.
      </Typography>
      <Typography variant="body2" gutterBottom>
        The most cost-effective rate was{" "}
        <strong>{cheapestSimulation.mortgage.interestRate}%</strong> with a
        total cost of{" "}
        <strong>{c(cheapestSimulation.totalCostOfMortgage)}</strong>.
      </Typography>
      <Typography variant="body2" gutterBottom sx={{ mb: 2 }}>
        Each 1% increase in interest rate costs approximately{" "}
        <strong>{c(costPerPercentPoint)}</strong> more over the life of the
        mortgage.
      </Typography>

      <TableContainer component={Paper} sx={{ mt: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>
                <strong>Interest Rate</strong>
              </TableCell>
              {isFixedTermMode ? (
                <TableCell align="right">
                  <strong>Monthly Payment</strong>
                </TableCell>
              ) : (
                <TableCell align="right">
                  <strong>Term Length</strong>
                </TableCell>
              )}
              <TableCell align="right">
                <strong>Total Cost</strong>
              </TableCell>
              <TableCell align="right">
                <Tooltip title="Interest paid as a percentage of the loan amount">
                  <strong>Interest Multiplier</strong>
                </Tooltip>
              </TableCell>
              <TableCell align="right">
                <strong>Total Interest</strong>
              </TableCell>
              {model.results.simulations.some(
                (sim) => sim.totalInterestEarned > 0,
              ) && (
                <TableCell align="right">
                  <Tooltip title="% of mortgage interest offset by savings & investments interest">
                    <strong>Savings Offset</strong>
                  </Tooltip>
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedByRate.map(
              ({
                sim,
                interestRate,
                termMonths,
                monthlyPayment,
                interestMultiplier,
                savingsPercentage,
              }) => (
                <TableRow
                  key={interestRate}
                  sx={{
                    backgroundColor:
                      sim === cheapestSimulation
                        ? "success.lighter"
                        : sim === highestRate.sim
                          ? "error.lighter"
                          : "inherit",
                  }}
                >
                  <TableCell sx={{ whiteSpace: "nowrap" }}>
                    {interestRate}%{sim === cheapestSimulation && <StarIcon />}
                  </TableCell>
                  <TableCell align="right">
                    {isFixedTermMode
                      ? c(monthlyPayment)
                      : yearsAndMonths(termMonths)}
                  </TableCell>
                  <TableCell align="right">
                    {c(sim.totalCostOfMortgage)}
                  </TableCell>
                  <TableCell align="right">
                    {(interestMultiplier * 100).toFixed(1)}%
                  </TableCell>
                  <TableCell align="right">
                    {c(sim.totalInterestPaid)}
                  </TableCell>
                  {model.results.simulations.some(
                    (s) => s.totalInterestEarned > 0,
                  ) && (
                    <TableCell align="right">
                      {sim.totalInterestEarned > 0
                        ? `${savingsPercentage}%`
                        : "-"}
                    </TableCell>
                  )}
                </TableRow>
              ),
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </ModeInsightsCard>
  );
}
